import {
  extractMentionUserIdFromHref,
  isMentionHref,
  normalizeMentionDisplayName,
} from '@/shared/utils/mentions'

export type CommunityMentionUser = {
  id: string
  full_name?: string | null
  avatar_url?: string | null
}

export type CommunityMentionUsersById = Record<
  string,
  Pick<CommunityMentionUser, 'id' | 'full_name' | 'avatar_url'>
>

export function renderCommunityMentionsInHtml(
  inputHtml: string,
  usersById?: CommunityMentionUsersById,
): string {
  const html = String(inputHtml || '').trim()
  if (!html) return ''
  if (!html.includes('/__mention__/user/')) return html
  if (typeof DOMParser === 'undefined') return html

  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  const anchors = Array.from(doc.querySelectorAll('a[href]'))

  anchors.forEach((anchor) => {
    const href = String(anchor.getAttribute('href') || '')
    if (!isMentionHref(href)) return

    const userId = extractMentionUserIdFromHref(href)
    const user = userId ? usersById?.[userId] : null
    const fallbackName = normalizeMentionDisplayName(anchor.textContent || '')
    const name = normalizeMentionDisplayName(user?.full_name || fallbackName)
    const label = `@${name}`
    const span = doc.createElement('span')
    if (userId) span.setAttribute('data-mention-user-id', userId)
    span.setAttribute(
      'class',
      'inline-flex items-center gap-1.5 rounded-md bg-sky-500/10 px-1.5 py-0.5 font-medium text-sky-300',
    )

    const avatarUrl = String(user?.avatar_url || '').trim()
    if (avatarUrl) {
      const img = doc.createElement('img')
      img.setAttribute('src', avatarUrl)
      img.setAttribute('alt', name)
      img.setAttribute('loading', 'eager')
      img.setAttribute('decoding', 'async')
      img.setAttribute('width', '16')
      img.setAttribute('height', '16')
      img.setAttribute('referrerpolicy', 'no-referrer')
      img.setAttribute(
        'class',
        'inline-block h-4 w-4 rounded-full object-cover align-[-0.1em]',
      )
      span.appendChild(img)
    } else {
      const fallback = doc.createElement('span')
      fallback.textContent = (name?.[0] || 'U').toUpperCase()
      fallback.setAttribute(
        'class',
        'inline-flex h-4 w-4 items-center justify-center rounded-full bg-sky-500/20 text-[10px] font-semibold text-sky-300',
      )
      span.appendChild(fallback)
    }

    const textNode = doc.createElement('span')
    textNode.textContent = label
    span.appendChild(textNode)

    try {
      anchor.replaceWith(span)
    } catch {
      // ignore
    }
  })

  return String(doc.body.innerHTML || '').trim()
}
